import { useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import {
    fetchAuditEvents,
    exportAuditEvents,
    type AuditEventQueryParams,
} from "../api/audit";
import "./audit-dashboard.css";

export const AuditDashboard = (): JSX.Element => {
    const [queryParams, setQueryParams] = useState<AuditEventQueryParams>({
        page: 1,
        pageSize: 50,
        sortBy: "CreatedAt",
        sortOrder: "desc",
    });

    const { data, error, isLoading, refetch } = useQuery({
        queryKey: ["audit", "events", queryParams],
        queryFn: () => fetchAuditEvents(queryParams),
        staleTime: 30 * 1000, // 30 seconds
    });

    const handleFilterChange = useCallback(
        (key: keyof AuditEventQueryParams, value: string | number | undefined) => {
            setQueryParams((prev) => ({
                ...prev,
                [key]: value || undefined,
                page: 1, // Reset to first page when filtering
            }));
        },
        []
    );

    const handleSort = useCallback(
        (sortBy: string) => {
            setQueryParams((prev) => ({
                ...prev,
                sortBy,
                sortOrder:
                    prev.sortBy === sortBy && prev.sortOrder === "asc" ? "desc" : "asc",
            }));
        },
        []
    );

    const handlePageChange = useCallback((page: number) => {
        setQueryParams((prev) => ({
            ...prev,
            page,
        }));
    }, []);

    const handleResetFilters = useCallback(() => {
        setQueryParams({
            page: 1,
            pageSize: 50,
            sortBy: "CreatedAt",
            sortOrder: "desc",
        });
    }, []);

    const handleExport = useCallback(async () => {
        try {
            const blob = await exportAuditEvents(queryParams);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `audit-events-${new Date().toISOString().split("T")[0]}.csv`;
            a.style.display = "none";

            // Safety check: ensure document.body exists before appending
            if (document.body) {
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            } else {
                // Fallback: try direct download if body is not available
                window.open(url, "_blank");
                window.URL.revokeObjectURL(url);
            }
        } catch (err) {
            console.error("Error exporting audit events:", err);
            alert("Failed to export audit events");
        }
    }, [queryParams]);

    if (error) {
        return (
            <div className="audit-dashboard">
                <h2>Audit Log</h2>
                <div className="audit-dashboard__error">
                    Error loading audit events. Please try again.
                </div>
            </div>
        );
    }

    return (
        <div className="audit-dashboard">
            <h2>Audit Log</h2>

            <div className="audit-filters">
                <div className="audit-filters__form">
                    <div className="audit-filters__field">
                        <label className="audit-filters__label" htmlFor="eventType">
                            Event Type
                        </label>
                        <input
                            id="eventType"
                            type="text"
                            className="audit-filters__input"
                            value={queryParams.eventType || ""}
                            onChange={(e) => handleFilterChange("eventType", e.target.value)}
                            placeholder="e.g., LoginSucceeded"
                        />
                    </div>

                    <div className="audit-filters__field">
                        <label className="audit-filters__label" htmlFor="startDate">
                            Start Date
                        </label>
                        <input
                            id="startDate"
                            type="datetime-local"
                            className="audit-filters__input"
                            value={
                                queryParams.startDate
                                    ? new Date(queryParams.startDate)
                                        .toISOString()
                                        .slice(0, 16)
                                    : ""
                            }
                            onChange={(e) =>
                                handleFilterChange(
                                    "startDate",
                                    e.target.value ? new Date(e.target.value).toISOString() : undefined
                                )
                            }
                        />
                    </div>

                    <div className="audit-filters__field">
                        <label className="audit-filters__label" htmlFor="endDate">
                            End Date
                        </label>
                        <input
                            id="endDate"
                            type="datetime-local"
                            className="audit-filters__input"
                            value={
                                queryParams.endDate
                                    ? new Date(queryParams.endDate).toISOString().slice(0, 16)
                                    : ""
                            }
                            onChange={(e) =>
                                handleFilterChange(
                                    "endDate",
                                    e.target.value ? new Date(e.target.value).toISOString() : undefined
                                )
                            }
                        />
                    </div>

                    <div className="audit-filters__field">
                        <label className="audit-filters__label" htmlFor="pageSize">
                            Page Size
                        </label>
                        <select
                            id="pageSize"
                            className="audit-filters__select"
                            value={queryParams.pageSize || 50}
                            onChange={(e) =>
                                handleFilterChange("pageSize", parseInt(e.target.value, 10))
                            }
                        >
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                            <option value={250}>250</option>
                            <option value={500}>500</option>
                        </select>
                    </div>

                    <div className="audit-filters__actions">
                        <button
                            type="button"
                            className="audit-filters__button"
                            onClick={() => refetch()}
                        >
                            Apply Filters
                        </button>
                        <button
                            type="button"
                            className="audit-filters__button audit-filters__button--reset"
                            onClick={handleResetFilters}
                        >
                            Reset
                        </button>
                    </div>
                </div>
            </div>

            {isLoading ? (
                <div className="audit-dashboard__loading">Loading audit events...</div>
            ) : (
                <>
                    <div className="audit-table-container">
                        <table className="audit-table">
                            <thead>
                                <tr>
                                    <th
                                        className="audit-table__sortable"
                                        onClick={() => handleSort("CreatedAt")}
                                    >
                                        Date
                                        {queryParams.sortBy === "CreatedAt" && (
                                            <span className="audit-table__sort-indicator">
                                                {queryParams.sortOrder === "asc" ? "↑" : "↓"}
                                            </span>
                                        )}
                                    </th>
                                    <th
                                        className="audit-table__sortable"
                                        onClick={() => handleSort("EventType")}
                                    >
                                        Event Type
                                        {queryParams.sortBy === "EventType" && (
                                            <span className="audit-table__sort-indicator">
                                                {queryParams.sortOrder === "asc" ? "↑" : "↓"}
                                            </span>
                                        )}
                                    </th>
                                    <th>Actor</th>
                                    <th>Target</th>
                                    <th>Team</th>
                                    <th>Metadata</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data?.items.map((event) => (
                                    <tr key={event.id}>
                                        <td className="audit-table__date">
                                            {new Date(event.createdAt).toLocaleString()}
                                        </td>
                                        <td>
                                            <span className="audit-table__event-type">
                                                {event.eventType}
                                            </span>
                                        </td>
                                        <td className="audit-table__user">
                                            {event.actorUserName || event.actorUserId || "-"}
                                        </td>
                                        <td className="audit-table__user">
                                            {event.targetUserName || event.targetUserId || "-"}
                                        </td>
                                        <td className="audit-table__user">
                                            {event.teamName || event.teamId || "-"}
                                        </td>
                                        <td>
                                            {event.metadata ? (
                                                <span className="audit-table__metadata" title={JSON.stringify(event.metadata)}>
                                                    {JSON.stringify(event.metadata).slice(0, 50)}
                                                    {JSON.stringify(event.metadata).length > 50 ? "..." : ""}
                                                </span>
                                            ) : (
                                                "-"
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {data && (
                        <>
                            <div className="audit-pagination">
                                <div className="audit-pagination__info">
                                    Showing {data.items.length > 0 ? (data.page - 1) * data.pageSize + 1 : 0} -{" "}
                                    {Math.min(data.page * data.pageSize, data.totalCount)} of{" "}
                                    {data.totalCount} events
                                </div>
                                <div className="audit-pagination__controls">
                                    <button
                                        type="button"
                                        className="audit-pagination__button"
                                        onClick={() => handlePageChange(data.page - 1)}
                                        disabled={data.page <= 1}
                                    >
                                        Previous
                                    </button>
                                    <span className="audit-pagination__info">
                                        Page {data.page} of {data.totalPages}
                                    </span>
                                    <button
                                        type="button"
                                        className="audit-pagination__button"
                                        onClick={() => handlePageChange(data.page + 1)}
                                        disabled={data.page >= data.totalPages}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>

                            <div className="audit-export">
                                <button
                                    type="button"
                                    className="audit-export__button"
                                    onClick={handleExport}
                                >
                                    Export to CSV
                                </button>
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
};